import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import {
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Typography,
  IconButton,
  Grid,
  InputAdornment,
  InputBasePropsSizeOverrides,
  CircularProgress,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import http from '../../commonFunctions/httpClient';
import { OverridableStringUnion } from '@mui/types'; // Import this from MUI types

import store from '../../StateManagement/store';

// Rendering every option as a full MenuItem is expensive for long lists (e.g.
// thousands of Items) — cap how many matches actually get mounted at once.
const MAX_VISIBLE_OPTIONS = 100;

type InputLabelPropsSizeOverrides = {};

type ControllSize = OverridableStringUnion<'small' | 'normal', InputLabelPropsSizeOverrides>
type selectSize = OverridableStringUnion<'small' | 'medium', InputBasePropsSizeOverrides>;

interface ControlledSelectFieldProps {
  label: any;
  id: string;
  name: string;
  className?: string;
  variant?: 'standard' | 'filled' | 'outlined';
  disabled?: boolean;
  control: any;
  defaultValue?: any;
  rules?: UseControllerProps['rules'];
  onChange?: (event: React.ChangeEvent<{ value: unknown }>) => void;
  required?: boolean;
  setValue?: any;
  reset?: any;
  type?: any;
  options?: { value: string | number | boolean; label: string, disabled?: boolean }[];
  description?: IDescription;
  labelType?: 'Material' | 'Normal';
  addButton?: boolean;
  handleAddButtonClick?: (e: any) => void;
  source?: {
    url: string;
    pageSize: number;
    page: number;
    filter: any[];
    sort: string[];
  };
  transFormData?: (data: any) => any;
  controllSize?: ControllSize
  selectSize?: selectSize,
  isMobile?: boolean
  inlineSearch?: boolean;
  // When true, matches if every typed word is a prefix of ANY word in the label
  // (e.g. "ceiling" matches "DSPA CEILING SPEAKER"), not just the start of the
  // whole label. Use for multi-word names like Item names; leave off for
  // fields like Customer/Technician where matching only the first name is wanted.
  matchAnyWord?: boolean;
}

interface IDescription {
  text: string | any;
  align: 'Top' | 'Bottom';
  style?: React.CSSProperties;
  className?: string;
}

const WSSelectField: React.FC<ControlledSelectFieldProps> = ({
  id,
  label,
  className,
  control,
  name,
  rules,
  variant = 'outlined',
  disabled = false,
  defaultValue = '',
  setValue,
  onChange,
  type,
  options,
  description,
  labelType = 'Material',
  addButton,
  handleAddButtonClick,
  source,
  transFormData,
  controllSize = "small",
  selectSize = "small",
  reset,
  isMobile = false,
  inlineSearch = false,
  matchAnyWord = false,
}) => {


  const [loading, setLoading] = useState<boolean>(source ? true : false);
  const [newOptions, setNewOptions] = useState<any>(options || []);

  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Filter options based on search term. Default: match only if the label
  // itself starts with what was typed — e.g. typing "b" matches "BEGIN" but
  // not "Robert Brown". With matchAnyWord, every typed word just needs to be
  // a prefix of SOME word in the label — e.g. "ceiling" matches
  // "DSPA CEILING SPEAKER" — for multi-part names like Item names.
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;

    const term = searchTerm.toLowerCase().trim();
    if (!term) return options;

    if (matchAnyWord) {
      const searchWords = term.split(/[^a-z0-9]+/i).filter(Boolean);
      if (searchWords.length === 0) return options;

      return options?.filter(option => {
        const labelWords = option.label.toLowerCase().split(/[^a-z0-9]+/i).filter(Boolean);
        return searchWords.every((sw) => labelWords.some((lw) => lw.startsWith(sw)));
      });
    }

    return options?.filter(option => option.label.toLowerCase().startsWith(term));
  }, [options, searchTerm, matchAnyWord]);

  // Rendering every matched option (which can be thousands for lists like Items)
  // as a full MenuItem is what made the dropdown feel slow to filter — cap how
  // many actually get mounted at once.
  const visibleOptions = useMemo(() => filteredOptions?.slice(0, MAX_VISIBLE_OPTIONS), [filteredOptions]);

  const handleSearchChange = (event: any) => {
    setSearchTerm(event.target.value);
  };

  const handleSelectOpen = () => {
    setIsOpen(true);
    setSearchTerm(''); // Reset search when opening
  };

  const handleSelectClose = () => {
    setIsOpen(false);
    setSearchTerm(''); // Reset search when closing
  };

  // const dispatch = useDispatch();

  useEffect(() => {
    if (options) {
      setNewOptions(options);
    }
  }, [options]);
  useEffect(() => {
    if (reset) {
      reset()
    }
  }, [reset]);

  // useEffect(() => {
  //   if (setValue) {
  //     setValue(name, defaultValue);
  //   }
  // }, [setValue, name, defaultValue]);

  const fetchDataFromAPI = async (
    page: number,
    pageSize: number,
    filter: any[] = [],
    sort: any[] = []
  ) => {
    try {
      // setLoading(true);
      if (source) {
        const response: any = await http.post(source?.url, {
          page: page,
          pageSize: pageSize,
          filter: filter,
          sort: sort,
        });
        return response;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      // setLoading(false); // End loading
    }
  };

  const loadPageData = useCallback(async () => {
    try {

      let response = await fetchDataFromAPI(1, 100000, source?.filter);
      setLoading(false)
      if (transFormData) {
        setNewOptions(transFormData(response?.data));
      } else {
        setNewOptions(response?.data);
      }
    } catch (error) {
      console.error('Error loading page data:', error);
    }
  }, [source, id, transFormData]);

  useEffect(() => {
    if (source && loadPageData && loading) {
      loadPageData();
    }
  }, [source, loadPageData]);

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const state = store.getState();
      if (state.components[id]?.data === null) {
        loadPageData();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [id, loadPageData]);

  const handleClick = (e: any) => {
    if (handleAddButtonClick) {
      handleAddButtonClick(e);
    }
  };
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field, fieldState }) => (
        <><Grid container spacing={1}>
          {!isMobile && <Grid item sm={addButton ? 11 : 12}>

            {}

            {/* Normal label rendered as a block above the field */}
            {labelType === 'Normal' && (
              <label
                htmlFor={id}
                style={{ display: 'block', marginBottom: '4px', fontSize: '0.78rem', fontWeight: 500, color: '#6b7280' }}
              >
                {label}
              </label>
            )}

            {description && description.align === 'Top' && (
              <Typography style={description.style}>{description.text}</Typography>
            )}

            {inlineSearch ? (
              /* inlineSearch keeps FormControl+Select for custom open/close handling */
              <FormControl
                className={className}
                disabled={disabled}
                variant={variant}
                fullWidth
                error={!!fieldState.error}
                sx={{
                  mb: 1,
                  '& .MuiInputLabel-root': {
                    fontSize: '0.85rem',
                    color: '#000',
                    transform: 'translate(14px, -29px) scale(1)',
                  },
                  '& .MuiOutlinedInput-notchedOutline legend': { width: 0 },
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                    borderRadius: '10px',
                    backgroundColor: '#f5f5f5',
                    fontSize: '0.85rem',
                    '& fieldset': { borderColor: '#d1d5db' },
                    '&:hover fieldset': { borderColor: '#9ca3af' },
                    '&.Mui-focused fieldset': { borderColor: '#6366f1', borderWidth: '1px' },
                  },
                }}
              >
                {labelType === 'Material' && (
                  <InputLabel id={id} size={controllSize} shrink>{label}</InputLabel>
                )}
                <Select
                  labelId={id}
                  id={id}
                  {...field}
                  size={selectSize}
                  onChange={(event: any) => { field.onChange(event); if (onChange) onChange(event); }}
                  onOpen={handleSelectOpen}
                  onClose={handleSelectClose}
                  open={isOpen}
                  label={labelType === 'Material' ? label : undefined}
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 320, maxWidth: 420 } },
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                  }}
                  renderValue={(value: any) => {
                    const selected = options?.find((o) => o.value === value);
                    return (
                      <Typography sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {selected?.label ?? ''}
                      </Typography>
                    );
                  }}
                >
                  <MenuItem style={{ padding: '8px', cursor: 'default' }}>
                    <TextField
                      size="small"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      fullWidth
                      autoFocus
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        sx: { fontSize: '0.75rem' },
                      }}
                      sx={{ m: 0, width: '100%', '& .MuiOutlinedInput-root': { padding: '4px 8px' } }}
                    />
                  </MenuItem>
                  {filteredOptions?.length === 0 && (
                    <MenuItem disabled sx={{ justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: '0.75rem', fontStyle: 'italic', color: 'text.secondary' }}>
                        No results found for "{searchTerm}"
                      </Typography>
                    </MenuItem>
                  )}
                  {visibleOptions?.map((option: any) => (
                    <MenuItem disabled={option?.disabled} key={option.value} value={option.value} sx={{ fontSize: '0.75rem' }}>
                      <Typography
                        title={option.label}
                        sx={{
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%',
                        }}
                      >
                        {option.label}
                      </Typography>
                    </MenuItem>
                  ))}
                  {filteredOptions && filteredOptions.length > MAX_VISIBLE_OPTIONS && (
                    <MenuItem disabled sx={{ justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: '0.7rem', fontStyle: 'italic', color: 'text.secondary' }}>
                        Showing {MAX_VISIBLE_OPTIONS} of {filteredOptions.length} — refine your search for more
                      </Typography>
                    </MenuItem>
                  )}
                </Select>
                {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
              </FormControl>
            ) : (
              /* TextField select — same internal structure as WSTextBox, guarantees row alignment */
              <div style={{ position: 'relative', marginBottom: '8px' }}>
                <TextField
                  select
                  id={id}
                  label={labelType === 'Material' ? label : undefined}
                  {...field}
                  size={selectSize}
                  disabled={disabled}
                  variant={variant}
                  fullWidth
                  className={className}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  onChange={(event: any) => { field.onChange(event); if (onChange) onChange(event as any); }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 36,
                      borderRadius: '10px',
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.85rem',
                      '& fieldset': { borderColor: '#d1d5db' },
                      '&:hover fieldset': { borderColor: '#9ca3af' },
                      '&.Mui-focused fieldset': { borderColor: '#6366f1', borderWidth: '1px' },
                    },
                    '& .MuiInputBase-input': { padding: '6px 12px', fontSize: '0.8rem' },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.85rem',
                      color: '#000',
                      transform: 'translate(14px, -29px) scale(1)',
                    },
                    '& .MuiOutlinedInput-notchedOutline legend': { width: 0 },
                  }}
                  SelectProps={{
                    MenuProps: { PaperProps: { style: { maxHeight: 320, maxWidth: 420 } } },
                  }}
                >
                  {newOptions?.map((option: any) => (
                    <MenuItem disabled={option?.disabled ?? false} key={option.value} value={option.value} sx={{ fontSize: '0.75rem' }}>
                      <Typography
                        title={option.label}
                        sx={{
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%',
                        }}
                      >
                        {option.label}
                      </Typography>
                    </MenuItem>
                  ))}
                </TextField>
                {loading && (
                  <CircularProgress
                    size={20}
                    style={{ position: 'absolute', right: '32px', top: '50%', transform: 'translateY(-50%)' }}
                  />
                )}
              </div>
            )}

            {description && description.align === 'Bottom' && (
              <Typography style={description.style} className={description?.className ?? undefined}>
                {description.text}
              </Typography>
            )}

          </Grid>}

          {}
          {addButton && (
            <Grid item sm={1} style={{ display: 'flex', alignItems: 'center' }} justifyContent={'end'}>
              <IconButton aria-label="add" onClick={(e: any) => handleClick(e)}>
                <AddIcon />
              </IconButton>
            </Grid>
          )}
        </Grid>
          {isMobile && <FormControl
            variant={variant}
            className={className}
            disabled={disabled}
            fullWidth
            //style={{minWidth:'25rem'}}
            style={{ width: '100%' }}
            error={!!fieldState.error}
          >
            {labelType === 'Material' && (
              <InputLabel
                id={id}
                size={controllSize}
                shrink={true}
                sx={{
                  fontSize: '0.78rem',
                  fontWeight: 500,
                  color: '#6b7280',
                  transform: 'translate(14px, -9px) scale(1)',
                  backgroundColor: '#f5f5f5',
                  padding: '0 4px',
                  borderRadius: '3px',
                  '&.Mui-focused': {
                    color: '#6366f1',
                  },
                }}
              >
                {label}
              </InputLabel>
            )}
            {description && description.align === 'Top' && (
              <Typography style={description.style}>{description.text}</Typography>
            )}
            {labelType === 'Normal' && (
  <label
    style={{
      display: 'block',
      marginBottom: '4px',
      fontSize: '0.78rem',
      color: '#6b7280',
      fontWeight: 500,
      letterSpacing: '0.02em',
    }}
    htmlFor={id}
  >
    {label}
  </label>
)}
            {description && description.align === 'Bottom' && (
              <Typography
                style={description.style}
                className={description?.className ? description?.className : undefined}
              >
                {description.text}
              </Typography>
            )}

            <Select
              labelId={id}
              id={id}
              {...field}
              
              size={selectSize}
              sx={{
    borderRadius: '10px',
    '& .MuiSelect-select': {
      padding: '10px 12px',
      display: 'flex',
      alignItems: 'center',
    },
  }}
              onChange={(event: any) => {
                field.onChange(event);
                if (onChange) {
                  onChange(event);
                }
              }}

              label={labelType === 'Material' ? label : undefined}
              MenuProps={{ PaperProps: { style: { maxHeight: 320, maxWidth: 420 } } }}
              inputProps={{
                endAdornment: addButton ? (
                  <InputAdornment position="end" className="suffixAdornment">
                    <IconButton aria-label="add" onClick={(e: any) => handleClick(e)}>
                      <AddIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,

              }}
            >
              {newOptions?.map((option: any) => (
                <MenuItem disabled={option?.disabled ? option?.disabled : false} key={option.value} value={option.value} sx={{ fontSize: '0.75rem' }}>
                  <Typography
                    title={option.label}
                    sx={{
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}
                  >
                    {option.label}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
            {loading && (
              <CircularProgress
                size={24}
                style={{
                  position: 'absolute', // Position the spinner absolutely
                  right: '20px', // Align it to the right of the select box
                  top: '30%', // Vertically center it
                  transform: 'translateY(-50%)',
                }}
              />
            )}
            {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
          </FormControl>}
        </>
      )}
    />
  );
};

export default WSSelectField;
